import { t } from './dictionary.js';
import { renderCard } from './card.js';
import { renderPagination } from './pagination.js';
import { getParam, setParam } from './url.js';

/**
 * Reads the current page number from the `page` URL query parameter.
 * @returns {number} The raw parsed value (may be out of range; clamping is done by the caller).
 */
function getPageFromUrl() {
  const parsed = parseInt(getParam('page'), 10);
  return Number.isFinite(parsed) ? parsed : 1;
}

/**
 * Clamps `page` to [1, totalPages] and cleans up the URL when the raw value
 * was out of range (negative/zero → remove param; too large → set to max).
 * @param {number} page
 * @param {number} totalPages
 * @returns {number}
 */
function clampPage(page, totalPages) {
  if (page < 1) {
    setParam('page', 1, { defaultValue: 1 });
    return 1;
  }
  if (page > totalPages) {
    setParam('page', totalPages, { defaultValue: 1 });
    return totalPages;
  }
  return page;
}

/**
 * Updates the `page` query parameter in the URL without reloading the page.
 * Removes the parameter entirely when on page 1.
 * @param {number} page - The page number to reflect in the URL.
 */
function updateUrl(page) {
  setParam('page', page, { defaultValue: 1, state: { page } });
}

/**
 * Builds the inner HTML for a single wind farm item.
 * @param {Object} item - The data item.
 * @returns {string}
 */
export function renderItem(item) {
  return renderCard({
    item,
    labels: t('list'),
  });
}

/**
 * Renders a page of wind farm items into the container element.
 * Creates a <ul> only when there are items; renders a <p> otherwise.
 * @param {Array}       items  - The items to display on the current page.
 * @param {HTMLElement} el     - The container element to render into.
 */
function renderItems({ el, items }) {
  [...el.children].forEach((child) => { if (child.tagName !== 'NAV') child.remove(); });

  if (items.length === 0) {
    const p = document.createElement('p');
    p.className = 'js-list-empty';
    p.textContent = t('noResults');
    el.prepend(p);
    return;
  }

  const ul = document.createElement('ul');
  ul.className = 'hq-list';

  items.forEach((item) => {
    const li = document.createElement('li');
    li.className = 'card';
    li.innerHTML = renderItem(item);
    ul.appendChild(li);
  });

  el.prepend(ul);
}

/**
 * Slices the full item list for the given page and renders it.
 * @param {Object} options
 * @param {Array}       options.items        - The full (unsliced) item array.
 * @param {HTMLElement} options.el           - The container element to render into.
 * @param {number}      options.currentPage  - The 1-based page to display.
 * @param {number}      options.itemsPerPage - Number of items per page.
 */
function renderPage({ currentPage, el, items, itemsPerPage }) {
  const start = (currentPage - 1) * itemsPerPage;
  const pageItems = items.slice(start, start + itemsPerPage);

  renderItems({ items: pageItems, el });
}

/**
 * Renders the paginated list and wires up URL and browser history.
 * @param {Object} options
 * @param {Array}       options.items        - The full item array.
 * @param {HTMLElement} options.el           - The container element to render into.
 * @param {number}      options.itemsPerPage - Number of items per page.
 */
export function renderList({ el, items, itemsPerPage }) {
  const totalPages = Math.ceil(items.length / itemsPerPage);
  const paginationEl = document.createElement('nav');
  el.appendChild(paginationEl);

  function goToPage(page, scroll = true) {
    updateUrl(page);
    renderPage({ items, el, currentPage: page, itemsPerPage });
    renderPagination({ currentPage: page, totalPages, el: paginationEl, onPageChange: goToPage });

    if (scroll) {
      const scrollTarget = el.closest('section') ?? el;
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  window.addEventListener('popstate', (event) => {
    const page = clampPage(event.state?.page ?? getPageFromUrl(), totalPages);
    goToPage(page, false);
  });

  goToPage(clampPage(getPageFromUrl(), totalPages), false);
}
